import { Dialog, Transition } from "@headlessui/react";
import { Fragment, HTMLAttributes, useEffect, useRef } from "react";
import { __ } from "@common/helpers/i18n";
import { icon } from "@common/helpers/icons";
import { ModalProps } from "@common/types";
import {
  ModalBodyPrimitive,
  ModalFooterPrimitive,
  ModalHeaderPrimitive,
} from "@app/components/ui/modal";
import { cn } from "@app/lib/utils";
import { Button } from "../form/Button";

type DialogType = typeof Dialog;

const sizes = {
  small: "w-full max-w-lg",
  medium: "w-full max-w-3xl",
  large: "w-full max-w-6xl",
  full: "w-full max-w-[calc(100vw-2rem)]",
};

const paddings = {
  small: "p-3",
  medium: "p-5",
  large: "p-7",
  none: "",
};

const openModalStack: symbol[] = [];

function ModalRoot({
  children,
  isOpen,
  onClose,
  showCloseIcon,
  size,
  panelClassName,
  viewportClassName,
  showOverflow,
  padding,
  ...rest
}: ModalProps & Parameters<DialogType>[0] & HTMLAttributes<HTMLDivElement>) {
  const modalId = useRef(Symbol("modal"));

  useEffect(() => {
    if (!isOpen) return;

    const currentModalId = modalId.current;
    openModalStack.push(currentModalId);

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;

      const topModalId = openModalStack[openModalStack.length - 1];
      if (topModalId !== currentModalId) return;

      event.preventDefault();
      onClose();
    };

    document.addEventListener("keydown", handleEscape, true);

    return () => {
      const stackIndex = openModalStack.lastIndexOf(currentModalId);
      if (stackIndex !== -1) {
        openModalStack.splice(stackIndex, 1);
      }
      document.removeEventListener("keydown", handleEscape, true);
    };
  }, [isOpen, onClose]);

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-modal" onClose={onClose} {...rest}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 z-modal bg-background/80 backdrop-blur-sm transition-opacity" />
        </Transition.Child>
        <div tabIndex={1} />
        <div
          className={cn(
            "app-modal__viewport fixed inset-0 z-modal-content flex h-[100dvh] items-start justify-center overflow-y-auto overscroll-contain px-4 py-0 sm:items-center",
            viewportClassName,
          )}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <Dialog.Panel
              className={cn(
                "relative min-h-0 max-h-full transform rounded-lg border border-border bg-card text-card-foreground shadow-medium outline-none transition-all",
                "app-modal__panel",
                sizes[size || "small"],
                panelClassName,
                padding && paddings[padding],
                showOverflow !== true && "overflow-hidden overflow-y-auto",
              )}
            >
              {showCloseIcon && (
                <Button
                  type="button"
                  padding="none"
                  variant="text"
                  className="absolute end-4 top-4 z-40 h-8 w-8 rounded-md text-muted-foreground hover:text-foreground"
                  onClick={onClose}
                  aria-label={__("Close")}
                >
                  {icon("x", { className: "h-3 w-3" })}
                </Button>
              )}
              {children}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

function Title({ className, ...props }: Parameters<DialogType["Title"]>[0]) {
  return (
    <Dialog.Title
      className={cn("text-lg font-bold leading-7 text-foreground", className)}
      {...props}
    />
  );
}

function Description(props: Parameters<DialogType["Description"]>[0]) {
  return <Dialog.Description {...props} />;
}

export const Modal = Object.assign(ModalRoot, {
  Title,
  Description,
  Header: ModalHeaderPrimitive,
  Body: ModalBodyPrimitive,
  Footer: ModalFooterPrimitive,
});
